import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSubscription, useSelectedAccountId } from '../../hooks/useSubscription'
import { useAccount } from '../../hooks/useAccount'
import { useAuth } from '../../hooks/useAuth'
import axiosInstance, { api } from '../../services/api'
import PlanCard from './PlanCard'
import AddonOption from './AddonOption'
import './SubscriptionPage.css'
import {
  trackViewSubscription,
  trackInitiateCheckout,
  generateEventId,
  collectBrowserContext,
  hashEmail,
  hashExternalId,
} from '../../utils/analytics'
import { getFeature, getComparisonFeatures, deduplicateFeatures } from './featureCatalog'

/**
 * Subscription management page showing available plans and current subscription
 *
 * Phase 5: Now includes add-on management with account-level add-on display
 */
const SubscriptionPage = () => {
  const { t, i18n } = useTranslation('subscription')
  const [plans, setPlans] = useState([])
  const [addons, setAddons] = useState([])
  const [activeAddons, setActiveAddons] = useState({}) // { addon_id: { expires_at, cancel_at_period_end, ... } }
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [billingPeriod, setBillingPeriod] = useState('monthly') // 'monthly' or 'yearly'
  const [processingCheckout, setProcessingCheckout] = useState(false)
  const [processingPortal, setProcessingPortal] = useState(false)

  // Phase 3: Get account context
  const accountId = useSelectedAccountId()
  const { selectedAccount } = useAccount()
  const { user } = useAuth()

  // Phase 3: Load subscription for selected account (falls back to user-level if no account)
  const { plan: currentPlan, billingCycle, status, hasStripeCustomer, mutate } = useSubscription(accountId)

  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    loadPlans()

    // Track ViewContent with Pixel (CAPI ViewContent is lower priority, Pixel suffices)
    trackViewSubscription(currentPlan)
  }, [])

  // Load active addons when account changes
  useEffect(() => {
    if (accountId && currentPlan) {
      loadActiveAddons()
    }
  }, [accountId, currentPlan])

  // Reload addons when returning from checkout success page
  useEffect(() => {
    if (location.state?.reloadAddons && accountId) {
      loadActiveAddons()
      // Clear the state to prevent re-fetching on subsequent renders
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state?.reloadAddons, accountId])

  const loadActiveAddons = async () => {
    try {
      // Fetch subscription info which includes active addons
      const subscriptionInfo = await api.getAccountSubscription(accountId)
      if (subscriptionInfo && subscriptionInfo.account_addons) {
        // Store full addon subscription data keyed by addon_id
        const addonsMap = {}
        subscriptionInfo.account_addons.forEach(addon => {
          addonsMap[addon.addon_id] = {
            expires_at: addon.expires_at,
            cancel_at_period_end: addon.cancel_at_period_end,
            is_active: addon.is_active,
            stripe_subscription_item_id: addon.stripe_subscription_item_id
          }
        })
        setActiveAddons(addonsMap)
      }
    } catch (err) {
      console.warn('Failed to load active addons:', err)
      // Don't set error state - this is not critical
      setActiveAddons({})
    }
  }

  // Helper to check if a plan has a specific feature
  const planHasFeature = (plan, featureId) => {
    const featureIds = Array.isArray(plan.feature_ids) ? plan.feature_ids : []
    return featureIds.includes(featureId)
  }

  // Helper to display feature check/cross
  const getFeatureDisplay = (hasFeature) => {
    return hasFeature ? '✓' : '—'
  }

  // Get feature name with translation fallback
  const getFeatureName = (featureId) => {
    const feature = getFeature(featureId)
    const translationKey = feature.translationKey
    const translated = t(translationKey, { defaultValue: null })
    return translated && translated !== translationKey ? translated : feature.name
  }

  /**
   * Determines if an add-on should be hidden from the user.
   * Returns true if addon should be HIDDEN.
   * Returns false if addon should be SHOWN.
   *
   * Reasons to hide an addon:
   * 1. User's plan doesn't meet the minimum plan requirement (min_plan_required)
   * 2. User's plan is in the addon's excluded_plans list (features already included)
   * 3. All its features are already in the user's current plan
   *
   * @param {Object} addon - The add-on object with feature_ids and min_plan_required
   * @returns {boolean} - True if addon should be hidden
   */
  const isAddonIncludedInPlan = (addon) => {
    if (!currentPlan || !plans.length) return false

    const currentPlanObj = plans.find(p => p.id === currentPlan)
    if (!currentPlanObj) {
      console.warn(`Current plan '${currentPlan}' not found in plans array. Cannot filter add-ons.`)
      return false // Show addon if we can't determine plan features
    }

    // Plan hierarchy: free < starter < pro
    const planHierarchy = { 'free': 0, 'starter': 1, 'pro': 2 }
    const currentPlanLevel = planHierarchy[currentPlan] || 0

    // Check 1: If addon has min_plan_required, user's plan must meet it
    if (addon.min_plan_required) {
      const minPlanLevel = planHierarchy[addon.min_plan_required] || 0
      if (currentPlanLevel < minPlanLevel) {
        console.debug(`Add-on '${addon.id}' hidden: requires plan '${addon.min_plan_required}', user has '${currentPlan}'`)
        return true // Hide addon - user doesn't meet minimum plan requirement
      }
    }

    // Check 2: If plan is in excluded_plans, hide the addon (features already included)
    const excludedPlans = addon.config?.excluded_plans || []
    if (excludedPlans.includes(currentPlan)) {
      console.debug(`Add-on '${addon.id}' hidden: plan '${currentPlan}' is in excluded_plans`)
      return true // Hide addon - user's plan already includes these features
    }

    // Check 3: If all features are already in the plan, hide the addon
    const addonFeatureIds = Array.isArray(addon.feature_ids) ? addon.feature_ids : []
    const planFeatureIds = Array.isArray(currentPlanObj.feature_ids) ? currentPlanObj.feature_ids : []

    // If addon has no features defined, we can't determine if it's redundant
    if (addonFeatureIds.length === 0) {
      console.warn(`Add-on '${addon.id}' has no feature_ids defined. Showing by default.`)
      return false // Show addon if features aren't defined
    }

    // Check if all features provided by the add-on are already in the plan
    const isIncluded = addonFeatureIds.every(featureId => planFeatureIds.includes(featureId))

    if (isIncluded) {
      console.debug(`Add-on '${addon.id}' hidden: all features already in plan '${currentPlan}'`)
    }

    return isIncluded
  }

  // Filter add-ons to only show those that aren't already included in the plan
  const displayableAddons = addons.filter(addon => !isAddonIncludedInPlan(addon))

  // Sort plans in conversion-optimized order: Pro → Starter → Free (Anchor Effect)
  const sortPlans = (plans) => {
    const planOrder = { pro: 0, starter: 1, free: 2 }
    return [...plans].sort((a, b) => {
      const orderA = planOrder[a.id] ?? 99
      const orderB = planOrder[b.id] ?? 99
      return orderA - orderB
    })
  }

  const loadPlans = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await axiosInstance.get('/subscription/plans')
      setPlans(sortPlans(response.data.plans || []))
      // Filter out custom_branding addon
      const filteredAddons = (response.data.addons || []).filter(addon => addon.id !== 'custom_branding')
      setAddons(filteredAddons)
    } catch (err) {
      console.error('Failed to load plans:', err)
      setError(t('page.errorLoadingPlans'))
    } finally {
      setLoading(false)
    }
  }

  const handleSelectPlan = async (planId) => {
    // Don't allow selecting current plan
    if (planId === currentPlan) {
      return
    }

    try {
      setProcessingCheckout(true)
      setError(null)

      // Find plan details for tracking
      const plan = plans.find(p => p.id === planId)
      const price = plan ? (billingPeriod === 'yearly' ? plan.price_yearly : plan.price_monthly) : 0

      // Generate CAPI event IDs for deduplication
      const initiateCheckoutEventId = generateEventId(accountId || 'user', 'InitiateCheckout')
      const capiEventId = generateEventId(accountId || 'user', 'Purchase')

      // Collect browser context ONCE - will be sent with checkout request
      // This ensures context is stored BEFORE redirect (solves timing issue)
      const browserContext = collectBrowserContext()

      // Track InitiateCheckout with Pixel (same eventId for deduplication)
      let hashedEmail = null
      let hashedExternalId = null
      if (user?.email && user?.id) {
        [hashedEmail, hashedExternalId] = await Promise.all([
          hashEmail(user.email),
          hashExternalId(user.id),
        ])
      }
      trackInitiateCheckout(planId, billingPeriod, price, {
        eventId: initiateCheckoutEventId,
        hashedEmail,
        hashedExternalId,
      })

      // Phase 3: Use account-level checkout if accountId available, otherwise user-level
      // Browser context is bundled with request - backend stores it before creating session
      const { url } = accountId
        ? await api.createAccountCheckoutSession(accountId, planId, billingPeriod, {
            capi_event_id: capiEventId,
            capi_context: browserContext,
          })
        : await api.createCheckoutSession(planId, billingPeriod)

      // Redirect to Stripe Checkout
      window.location.href = url
    } catch (err) {
      console.error('Failed to create checkout session:', err)
      setError(err.message || t('page.errorCheckout'))
      setProcessingCheckout(false)
    }
  }

  const handleManageBilling = async () => {
    try {
      setProcessingPortal(true)
      setError(null)

      // Create Customer Portal session
      const returnUrl = `${window.location.origin}/subscription`
      const { url } = await api.createPortalSession(returnUrl)

      // Redirect to Stripe Customer Portal
      window.location.href = url
    } catch (err) {
      console.error('Failed to open billing portal:', err)
      setError(err.message || t('page.errorPortal'))
      setProcessingPortal(false)
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="subscription-page">
        <div className="page-header">
          <h1>{t('page.title')}</h1>
          <p>{t('page.loadingPlans')}</p>
        </div>
        <div className="plans-grid">
          {[1, 2, 3].map((i) => (
            <div key={i} className="plan-skeleton" />
          ))}
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="subscription-page">
        <div className="page-header">
          <h1>{t('page.title')}</h1>
          <div className="error-state">
            <p className="error-message">{error}</p>
            <button className="retry-button" onClick={loadPlans}>
              {t('page.retryButton')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="subscription-page">
      {/* Header */}
      <div className="page-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          {t('page.backButton')}
        </button>
        <h1>{t('page.title')}</h1>
        <p className="subtitle">
          {selectedAccount
            ? t('page.subtitleForAccount', { account: selectedAccount.instagram_username || selectedAccount.username, defaultValue: `Subscription for @${selectedAccount.instagram_username || selectedAccount.username}` })
            : t('page.subtitle')
          }
        </p>
      </div>

      {/* Current Plan Badge with Manage Button */}
      {currentPlan && (
        <div className="current-plan-banner">
          <div className="banner-content">
            <div className="banner-info">
              <span className="banner-icon">✨</span>
              <div className="banner-text">
                <span className="plan-line">
                  {t('page.currentPlan.banner', { plan: currentPlan.toUpperCase() })}
                </span>
                {status === 'active' && billingCycle?.end && (() => {
                  try {
                    const date = new Date(billingCycle.end)
                    if (isNaN(date.getTime())) return null
                    return (
                      <span className="billing-date">
                        {t('page.currentPlan.nextBilling')}: {date.toLocaleDateString(i18n.language === 'de' ? 'de-DE' : 'en-US', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </span>
                    )
                  } catch (e) {
                    return null
                  }
                })()}
              </div>
            </div>
          </div>
          {/* Only show Manage Billing button if user has a Stripe customer ID (i.e., had a subscription before) */}
          {hasStripeCustomer && (
            <button
              className="manage-billing-btn"
              onClick={handleManageBilling}
              disabled={processingPortal}
            >
              {processingPortal ? t('page.manageBilling.loading') : t('page.manageBilling.button')}
            </button>
          )}
        </div>
      )}

      {/* Billing Period Toggle */}
      <div className="billing-toggle-container">
        <button
          className={`billing-toggle-btn ${billingPeriod === 'monthly' ? 'active' : ''}`}
          onClick={() => setBillingPeriod('monthly')}
          disabled={processingCheckout}
        >
          {t('page.billingPeriod.monthly')}
        </button>
        <button
          className={`billing-toggle-btn ${billingPeriod === 'yearly' ? 'active' : ''}`}
          onClick={() => setBillingPeriod('yearly')}
          disabled={processingCheckout}
        >
          {t('page.billingPeriod.yearly')}
          <span className="savings-badge">{t('page.billingPeriod.savings')}</span>
        </button>
      </div>

      {/* Plans Grid */}
      <div className="plans-grid">
        {plans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            billingPeriod={billingPeriod}
            isCurrentPlan={plan.id === currentPlan}
            onSelect={handleSelectPlan}
            highlighted={location.state?.highlightFeature}
            disabled={processingCheckout}
            hasActiveSubscription={currentPlan && currentPlan !== 'free'}
          />
        ))}
      </div>

      {/* Add-ons Section - Visible for all plans with available add-ons */}
      {displayableAddons.length > 0 && currentPlan && (
        <div className="addons-section">
          <h2>{t('page.addons.title')}</h2>
          <p className="section-subtitle">
            {t('page.addons.subtitle')}
          </p>

          <div className="addons-grid">
            {displayableAddons.map((addon) => (
              <AddonOption
                key={addon.id}
                addon={addon}
                isActive={!!activeAddons[addon.id]}
                subscriptionData={activeAddons[addon.id] || null}
                onSubscriptionChange={(addonId, isActive, cancellationResult) => {
                  // Update active addons when subscription status changes
                  if (isActive) {
                    setActiveAddons({
                      ...activeAddons,
                      [addonId]: { is_active: true }
                    })
                  } else if (cancellationResult) {
                    // Addon was cancelled - update with cancellation info
                    setActiveAddons({
                      ...activeAddons,
                      [addonId]: {
                        ...activeAddons[addonId],
                        cancel_at_period_end: true,
                        expires_at: cancellationResult.cancellation_effective_at
                          ? new Date(cancellationResult.cancellation_effective_at * 1000).toISOString()
                          : null
                      }
                    })
                  } else {
                    // Remove addon completely
                    const { [addonId]: removed, ...rest } = activeAddons
                    setActiveAddons(rest)
                  }
                }}
                disabled={processingCheckout || processingPortal}
              />
            ))}
          </div>
          <p className="addons-note">
            {t('page.addons.note')}
          </p>
        </div>
      )}

      {/* Features Comparison - Dynamic based on actual feature_ids */}
      {plans.length > 0 && (() => {
        // Get all unique features from all paid plans (plans with Stripe IDs)
        const paidPlans = plans.filter(p => p.id !== 'free')
        const allFeaturesSet = new Set()
        paidPlans.forEach(plan => {
          const featureIds = Array.isArray(plan.feature_ids) ? plan.feature_ids : []
          featureIds.forEach(id => allFeaturesSet.add(id))
        })

        // Get comparison features (exclude base/addon languages and analytics)
        let comparisonFeatures = Array.from(allFeaturesSet).filter(featureId => {
          const feature = getFeature(featureId)
          // Show: media, caption, scheduling, integration, support, branding
          // Hide: base_language, language_addon, analytics
          const showCategories = ['media', 'caption', 'scheduling', 'ecommerce', 'integration', 'support', 'branding']
          return showCategories.includes(feature.category)
        }).sort((a, b) => {
          const featureA = getFeature(a)
          const featureB = getFeature(b)
          // Sort by category, then by name
          const categoryOrder = { media: 0, caption: 1, scheduling: 2, ecommerce: 3, integration: 4, support: 5, branding: 6 }
          const catDiff = (categoryOrder[featureA.category] || 99) - (categoryOrder[featureB.category] || 99)
          return catDiff !== 0 ? catDiff : featureA.name.localeCompare(featureB.name)
        })

        // Deduplicate features by name (e.g., emoji_control and hashtag_control -> show once)
        comparisonFeatures = deduplicateFeatures(comparisonFeatures)

        // Plan limits data for comparison table
        const planLimits = {
          pro: {
            storiesPerMonth: t('page.comparison.values.unlimited'),
            postingInterval: `2 ${t('page.comparison.values.hours')}`,
            videoLength: `30 ${t('page.comparison.values.seconds')}`,
            captionRegenerations: t('page.comparison.values.unlimited'),
            languages: t('page.comparison.values.allEU'),
            productVideos: '✓',
            productFeeds: '✓',
            linkInBio: '✓'
          },
          starter: {
            storiesPerMonth: '100',
            postingInterval: `7 ${t('page.comparison.values.hours')}`,
            videoLength: `15 ${t('page.comparison.values.seconds')}`,
            captionRegenerations: `30${t('page.comparison.values.perStory')}`,
            languages: t('page.comparison.values.allEU'),
            productVideos: '✓',
            productFeeds: t('page.comparison.values.none'),
            linkInBio: '✓'
          },
          free: {
            storiesPerMonth: '10',
            postingInterval: `24 ${t('page.comparison.values.hours')}`,
            videoLength: t('page.comparison.values.none'),
            captionRegenerations: `1${t('page.comparison.values.perStory')}`,
            languages: t('page.comparison.values.deEn'),
            productVideos: t('page.comparison.values.none'),
            productFeeds: t('page.comparison.values.none'),
            linkInBio: t('page.comparison.values.none')
          }
        }

        const limitRows = [
          { key: 'storiesPerMonth', label: t('page.comparison.limits.storiesPerMonth') },
          { key: 'postingInterval', label: t('page.comparison.limits.postingInterval') },
          { key: 'videoLength', label: t('page.comparison.limits.videoLength') },
          { key: 'captionRegenerations', label: t('page.comparison.limits.captionRegenerations') },
          { key: 'languages', label: t('page.comparison.limits.languages') },
          { key: 'productVideos', label: t('page.comparison.limits.productVideos') },
          { key: 'productFeeds', label: t('page.comparison.limits.productFeeds') },
          { key: 'linkInBio', label: t('page.comparison.limits.linkInBio') }
        ]

        return (
          <div className="features-comparison">
            <h2>{t('page.comparison.title')}</h2>
            <div className="comparison-table">
              {/* Header Row */}
              <div className="comparison-row header">
                <div className="feature-name">{t('page.comparison.featureHeader')}</div>
                {plans.map((plan) => {
                  const translatedName = t(`planCard.plans.${plan.id}.name`, { defaultValue: plan.name })
                  return (
                    <div key={plan.id} className="plan-column">
                      {translatedName}
                    </div>
                  )
                })}
              </div>

              {/* Limits Rows - Key decision factors */}
              {limitRows.map((row) => (
                <div key={row.key} className="comparison-row limits-row">
                  <div className="feature-name">{row.label}</div>
                  {plans.map((plan) => (
                    <div key={`${plan.id}-${row.key}`} className="plan-column">
                      {planLimits[plan.id]?.[row.key] || t('page.comparison.values.none')}
                    </div>
                  ))}
                </div>
              ))}

              {/* Additional Features */}
              <div className="comparison-row">
                <div className="feature-name">{t('features.gif_rendering')}</div>
                {plans.map((plan) => (
                  <div key={`${plan.id}-gif`} className="plan-column">
                    {planHasFeature(plan, 'gif_rendering') ? '✓' : '—'}
                  </div>
                ))}
              </div>
              <div className="comparison-row">
                <div className="feature-name">{t('features.pro_fonts')}</div>
                {plans.map((plan) => (
                  <div key={`${plan.id}-fonts`} className="plan-column">
                    {planHasFeature(plan, 'pro_fonts') ? '✓' : '—'}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )
      })()}

      {/* FAQ Section */}
      <div className="faq-section">
        <h2>{t('page.faq.title')}</h2>
        {t('page.faq.subtitle') && <p className="faq-subtitle">{t('page.faq.subtitle')}</p>}

        {(() => {
          // Access i18n resources directly instead of using returnObjects
          const currentLanguage = i18n.language
          const resources = i18n.getResourceBundle(currentLanguage, 'subscription')
          const categories = resources?.page?.faq?.categories

          // Check if categories is a valid object (not a string or undefined)
          if (!categories || typeof categories !== 'object' || typeof categories === 'string') {
            console.error('FAQ categories not loaded correctly:', categories)
            return null
          }

          return Object.entries(categories).map(([categoryKey, category]) => (
            <div key={categoryKey} className="faq-category">
              <h3 className="faq-category-title">
                {category.icon && <span className="faq-category-icon">{category.icon}</span>}
                {category.title}
              </h3>

              <div className="faq-list">
                {(Array.isArray(category.faqs) ? category.faqs : []).map((faq, index) => (
                  <div key={index} className="faq-item">
                    <h4 className="faq-question">{faq.question}</h4>
                    <p className="faq-answer">
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ))
        })()}

        {/* Support CTA */}
        {t('page.faq.supportCta') && (
          <div className="faq-support-cta">
            <p>{t('page.faq.supportCta.text')}</p>
            <a href={t('page.faq.supportCta.linkUrl')} className="faq-support-link">
              {t('page.faq.supportCta.linkText')}
            </a>
          </div>
        )}
      </div>

      {/* CTA Section */}
      <div className="cta-section">
        <h2>{t('page.cta.heading')}</h2>
        <p>{t('page.cta.message')}</p>
      </div>
    </div>
  )
}

export default SubscriptionPage
